import styled, { css } from 'styled-components'

const viewedPrimaryColor = '#858c90'
const viewedSecondaryColor = '#8c9498'
const viewedTertiaryColor = '#949ca0'

const viewedIndicator = css`
  ${({
    theme: {
      table: { defaultPrimaryColor },
    },
  }) => css`
    :before {
      content: '';
      width: 10px;
      height: 10px;
      background: ${defaultPrimaryColor};
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `}
`
export const TableStyle = styled.table`
  ${({
    theme: {
      table: {
        defaultPrimaryColor,
        dividerColor,
        rowColor,
        viewedRowColor,
        textColor,
        mutedTextColor,
        tableBorderColor,
        tableShadow,
        actionColor,
        actionBorderColor,
        actionBackground,
        actionHoverBackground,
      },
    },
  }) => css`
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    margin: 25px 0;
    font-size: 0.9em;
    width: 100%;
    border: 1px solid ${tableBorderColor};
    border-radius: 14px;
    overflow: hidden;
    box-shadow: ${tableShadow};
    color: ${textColor};

    thead tr {
      background: ${defaultPrimaryColor};
      color: #fff;
      text-align: left;
      text-transform: uppercase;
    }

    th {
      height: 42px;
      padding: 0 14px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.15;
      letter-spacing: 0.15px;
      vertical-align: middle;
    }

    th,
    td {
      box-sizing: border-box;
    }

    td {
      height: 96px;
      padding: 12px 14px;
      vertical-align: middle;
    }

    tbody tr {
      background: ${rowColor};

      &:not(:last-of-type) td {
        border-bottom: 1px solid ${dividerColor};
      }

      &.viewed-file-row {
        background: ${viewedRowColor};
      }
    }

    .viewed-column {
      width: 78px;
      padding-left: 8px;
      padding-right: 8px;
      text-align: center;
    }

    .name-column {
      width: auto;
      overflow-wrap: anywhere;
      line-height: 1.45;
    }

    td.name-column {
      color: ${textColor};
      font-weight: 500;
    }

    .meta-column {
      width: 70px;
      text-align: center;
      white-space: nowrap;
    }

    .resolution-column {
      width: 92px;
      text-align: center;
      white-space: nowrap;
    }

    .size-column {
      width: 105px;
      white-space: nowrap;
    }

    td.size-column {
      color: ${mutedTextColor};
    }

    .actions-column {
      width: 332px;
    }

    td.viewed-file-indicator {
      position: relative;
      ${viewedIndicator}
    }

    .button-cell {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: stretch;
      gap: 8px;

      > *,
      > a > button {
        width: 100%;
        min-width: 0;
        min-height: 36px;
      }

      > :last-child:nth-child(odd) {
        grid-column: 1 / -1;
      }

      button {
        padding: 5px 9px;
        line-height: 1.15;
      }

      .MuiButton-outlined {
        color: ${actionColor};
        border-color: ${actionBorderColor};
        background: ${actionBackground};

        &:hover {
          border-color: ${actionColor};
          background: ${actionHoverBackground};
        }
      }
    }

    @media (max-width: 970px) {
      display: none;
    }
  `}
`

export const ShortTableWrapper = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(2, 1fr);
  display: none;

  @media (max-width: 970px) {
    display: grid;
  }

  @media (max-width: 820px) {
    gap: 15px;
    grid-template-columns: 1fr;
  }
`

export const ShortTable = styled.div`
  ${({
    isViewed,
    theme: {
      table: { defaultPrimaryColor, defaultSecondaryColor, defaultTertiaryColor },
    },
  }) => css`
    width: 100%;
    grid-template-rows: repeat(3, max-content);
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);

    .short-table {
      &-name {
        background: ${isViewed ? viewedPrimaryColor : defaultPrimaryColor};
        display: grid;
        place-items: center;
        padding: 15px;
        color: #fff;
        text-transform: uppercase;
        font-size: 15px;
        font-weight: bold;

        @media (max-width: 880px) {
          font-size: 13px;
          padding: 10px;
        }
      }
      &-data {
        display: grid;
        grid-auto-flow: column;
        grid-template-columns: ${isViewed ? 'max-content' : '1fr'};
        grid-auto-columns: 1fr;
      }
      &-field {
        display: grid;
        grid-template-rows: 30px 1fr;
        background: black;
        :not(:last-child) {
          border-right: 1px solid ${isViewed ? viewedPrimaryColor : defaultPrimaryColor};
        }

        &-name {
          background: ${isViewed ? viewedSecondaryColor : defaultSecondaryColor};
          color: #fff;
          text-transform: uppercase;
          font-size: 12px;
          font-weight: 500;
          display: grid;
          place-items: center;
          padding: 0 10px;

          @media (max-width: 880px) {
            font-size: 11px;
          }
        }

        &-value {
          background: ${isViewed ? viewedTertiaryColor : defaultTertiaryColor};
          display: grid;
          place-items: center;
          color: #fff;
          font-size: 15px;
          padding: 15px 10px;
          position: relative;

          @media (max-width: 880px) {
            font-size: 13px;
            padding: 12px 8px;
          }
        }
      }

      &-viewed-indicator {
        ${isViewed && viewedIndicator}
      }

      &-buttons {
        padding: 20px;
        border-bottom: 2px solid ${isViewed ? viewedPrimaryColor : defaultPrimaryColor};
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        align-items: center;
        gap: 20px;
        background: #f3f3f3;

        @media (max-width: 410px) {
          gap: 10px;
          grid-template-columns: 1fr;
        }
      }
    }
  `}
`
